// src/agent-client.ts
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { z } from "zod/v4";
import { propagation, context as otelContext } from "@opentelemetry/api";

// src/tool-output.ts
function isToolOutput(obj) {
  return typeof obj === "object" && obj !== null && "llmMessage" in obj && typeof obj.llmMessage === "string" && "richContent" in obj && Array.isArray(obj.richContent);
}
function generateOutput(generator) {
  const llmMessages = [];
  const richContent = [];
  for (const content of generator()) {
    if (typeof content === "string") {
      llmMessages.push(content);
    } else {
      richContent.push(...content.richContent);
      llmMessages.push(content.llmMessage);
    }
  }
  return { richContent, llmMessage: llmMessages.join("\n") };
}
function textFromMaybeTemplate(strings, ...values) {
  if (typeof strings === "string")
    return strings;
  let fullText = "";
  strings.forEach((string, i) => {
    fullText += string;
    if (i < values.length) {
      fullText += values[i];
    }
  });
  const text = fullText.trim();
  return text;
}
function textOutput(strings, ...values) {
  const text = textFromMaybeTemplate(strings, ...values);
  return {
    richContent: [{ type: "text", text }],
    llmMessage: text
  };
}
function messageOutput(strings, ...values) {
  const text = textFromMaybeTemplate(strings, ...values);
  return {
    richContent: [],
    llmMessage: text
  };
}
function errorOutput(strings, ...values) {
  const text = textFromMaybeTemplate(strings, ...values);
  return {
    richContent: [],
    llmMessage: text,
    error: true
  };
}

// src/agent-client.ts
var CLIENT_VERSION = 1;
var DEFAULT_OPTIONS = {
  maxToolCalls: 15
};
var PaymentRequiredError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PaymentRequiredError";
  }
};
async function getErrorForResponse(response) {
  if (response.ok) {
    return void 0;
  } else {
    const text = await response.text();
    if (response.status === 402) {
      return new PaymentRequiredError(`Agent: ${response.status} ${response.statusText}: ${text}`);
    }
    return new Error(`Agent: ${response.status} ${response.statusText}: ${text}`);
  }
}
var rowValueSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
var columnTypeSchema = z.enum(["string", "number", "boolean", "image-uri", "uri", "date", "date-time"]);
var rowSchema = z.record(
  z.string(),
  z.object({
    type: columnTypeSchema,
    description: z.string().optional(),
    displayName: z.string().optional()
    // defaults to the internal name
  })
);
var AUTHENTICATION_TIMEOUT_ERROR = "Authentication timeout";
var AgentClient = class {
  history;
  options;
  baseURL;
  headersPromise;
  currentRunId;
  async getHeaders() {
    if (this.headersPromise !== void 0) {
      return await this.headersPromise;
    }
    const authTimeoutMs = this.options.authTimeoutMs ?? 1e4;
    const startTime = Date.now();
    try {
      const idToken = await Promise.race([
        this.options.getUserIdToken(),
        new Promise(
          (_, reject) => setTimeout(() => reject(new Error(AUTHENTICATION_TIMEOUT_ERROR)), authTimeoutMs)
        )
      ]);
      if (idToken === void 0)
        throw new Error("Failed to get current user");
      const headers = {
        ...this.options.headers,
        Authorization: `Bearer ${idToken}`
      };
      return headers;
    } catch (error) {
      if (error instanceof Error && error.message === AUTHENTICATION_TIMEOUT_ERROR) {
        this.recordTimeoutEvent("auth_timeout", Date.now() - startTime);
      }
      throw error;
    }
  }
  constructor(options) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    const { endpoint, history, getGatewayURL } = this.options;
    const gatewayURL = getGatewayURL("agent-service");
    this.baseURL = new URL(endpoint.startsWith("/") ? endpoint.substring(1) : endpoint, `${gatewayURL}/agents/`).toString() + "/";
    this.headersPromise = this.getHeaders();
    this.history = history ?? [];
  }
  currentRunIdDefined() {
    if (this.currentRunId === void 0) {
      this.currentRunId = Math.random().toString(36).substring(2, 15);
    }
    return this.currentRunId;
  }
  recordTimeoutEvent(type, durationMs) {
    console.warn(`Agent timeout: ${type}, duration: ${durationMs}ms, runId: ${this.currentRunId}`);
    this.options.onTimeout?.(type, durationMs, this.currentRunId);
  }
  reset() {
    this.history.length = 0;
    this.currentRunId = void 0;
  }
  /**
   * This is the main method for running the agent on a message from the user.
   *
   * @param content - The user message to run the agent on.
   * @returns A generator of the agent's responses.
   */
  async *run(request, abortSignal, frontendCb = void 0) {
    this.currentRunId = this.currentRunIdDefined();
    if ("userMessage" in request) {
      const filesContent = (request.files ?? []).length > 0 ? `

FileUrls: ${JSON.stringify(request.files)}` : "";
      const userMessage = {
        type: "message",
        role: "user",
        content: request.userMessage + filesContent
      };
      this.history.push(userMessage);
    } else {
      this.history.push(...request.messages);
    }
    yield* this.runAgentLoop(0, abortSignal, frontendCb);
  }
  /**
   * Run the agent on a message from the user, collecting all the messages into a single string.
   * Resets the agent's history before running.
   *
   * @param message - The user message to run the agent on.
   * @returns A promise of the agent's response.
   */
  async runFull(request, abortSignal, frontendCb = void 0) {
    this.reset();
    return collectOutput(this.run(request, abortSignal, frontendCb));
  }
  /**
   * This is the main loop for running the agent.
   * It handles the streaming of events from the server and the processing of the agent's responses.
   * It calls itself recursively if the agent calls any tools.
   * It terminates early if the agent makes too many tool calls or if the agent calls a tool
   * that cannot be handled by the server (e.g. a builder tool).
   */
  async *runAgentLoop(previousToolCalls = 0, abortSignal, frontendCb = void 0) {
    const body = JSON.stringify({
      history: this.history,
      context: await this.options.getContext(),
      runId: this.currentRunIdDefined()
    });
    let receivingEvents = true;
    const events = [];
    let onError;
    let onNextEvent;
    let nextEventPromise = function makePromise() {
      return new Promise((resolve, reject) => {
        onNextEvent = (evt) => {
          if (evt !== void 0) {
            events.push(evt);
            nextEventPromise = makePromise();
          } else {
            receivingEvents = false;
          }
          resolve();
        };
        onError = reject;
      });
    }();
    const url = new URL("turn", this.baseURL);
    const headers = await this.headersPromise;
    propagation.inject(otelContext.active(), headers);
    const controller = new AbortController();
    const startTime = Date.now();
    const connectionTimeoutMs = this.options.streamConnectionTimeoutMs ?? 15e3;
    let hasConnected = false;
    let connectionTimeout = setTimeout(() => {
      if (!hasConnected) {
        controller.abort();
        this.recordTimeoutEvent("connection_timeout", Date.now() - startTime);
        onError(new Error("Stream connection timeout"));
      }
    }, connectionTimeoutMs);
    abortSignal.addEventListener("abort", () => controller.abort());
    const fetchPromise = fetchEventSource(url.toString(), {
      method: "POST",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        // TODO: Remove this once the agent service is deployed
        // without it.
        "x-glide-agent-support-concurrent": "true",
        "x-glide-agent-client-version": CLIENT_VERSION.toString(),
        ...headers
      },
      body,
      // Don't disconnect and reconnect based on page visibility.
      openWhenHidden: true,
      onmessage: ({ data }) => {
        onNextEvent(JSON.parse(data));
      },
      onopen: async (res) => {
        hasConnected = true;
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
          connectionTimeout = void 0;
        }
        const error = await getErrorForResponse(res);
        if (error !== void 0) {
          onError(error);
          throw error;
        }
      },
      onclose: () => {
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
          connectionTimeout = void 0;
        }
        onNextEvent(void 0);
      },
      onerror: (err) => {
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
          connectionTimeout = void 0;
        }
        onError(err);
        throw err;
      }
    });
    const unhandledToolCalls = /* @__PURE__ */ new Map();
    while (receivingEvents || events.length > 0) {
      const event = events.shift();
      if (event === void 0) {
        await nextEventPromise;
        continue;
      }
      switch (event.type) {
        case "message":
          this.history.push({
            role: "assistant",
            ...event
          });
          yield event;
          break;
        case "message_progress":
          yield event;
          break;
        case "function_call":
          unhandledToolCalls.set(event.call_id, event);
          if (this.options.toolCallStart !== void 0) {
            this.options.toolCallStart({
              id: event.call_id,
              tool: event.name,
              input: event.arguments,
              displayName: event.display_name
            });
          }
          break;
        case "function_call_output": {
          const callEvent = unhandledToolCalls.get(event.call_id);
          if (callEvent !== void 0) {
            if (callEvent.add_to_history !== false) {
              this.history.push(callEvent, event);
            }
            unhandledToolCalls.delete(event.call_id);
            if (this.options.toolCallFinish !== void 0) {
              this.options.toolCallFinish({
                id: event.call_id,
                tool: callEvent.name,
                input: callEvent.arguments,
                result: event.output
              });
            }
          }
          break;
        }
        case "frontend_effect":
          frontendCb?.(event.name, event.arguments);
          break;
        default:
          console.warn("Agent: Unknown event type", event);
          break;
      }
    }
    await fetchPromise;
    yield* this.processToolCallsAndMaybeLoop(unhandledToolCalls, abortSignal, previousToolCalls);
  }
  async *processToolCallsAndMaybeLoop(unhandledToolCalls, abortSignal, previousToolCalls = 0) {
    const maxToolCalls = this.options.maxToolCalls;
    if (previousToolCalls > maxToolCalls)
      return;
    let toolCalls = previousToolCalls;
    for (const event of Array.from(unhandledToolCalls.values())) {
      let output;
      const toolResult = await this.options.callTool(event.name, event.arguments);
      if (toolResult !== false) {
        output = toolResult;
      }
      if (this.options.toolCallFinish !== void 0) {
        this.options.toolCallFinish({
          id: event.call_id,
          tool: event.name,
          input: event.arguments,
          result: output
        });
      }
      if (output !== void 0) {
        if (event !== void 0) {
          this.history.push(event);
        }
        if (event.call_id !== void 0) {
          this.history.push({
            type: "function_call_output",
            call_id: event.call_id,
            output
          });
          unhandledToolCalls.delete(event.call_id);
        }
      }
      toolCalls++;
      if (toolCalls >= maxToolCalls)
        break;
    }
    for (const event of unhandledToolCalls.values()) {
      this.history.push(event, {
        type: "function_call_output",
        call_id: event.call_id,
        output: messageOutput`Tool definition not found for tool name: ${event.name}`
      });
    }
    if (toolCalls > previousToolCalls) {
      yield* this.runAgentLoop(toolCalls, abortSignal);
    }
  }
  /**
   * Get next message suggestions that the agent can present to the user.
   *
   * @param count - The number of suggestions to get.
   * @returns A promise of the suggestions.
   */
  async getNextMessageSuggestions(count = 2) {
    const url = new URL("suggest", this.baseURL);
    const context = await this.options.getContext();
    const body = {
      history: this.history,
      context,
      count
    };
    try {
      const { data } = await this.post(url.toString(), body);
      return data;
    } catch {
      return [];
    }
  }
  /**
   * Get initial message suggestions that the agent can present to the user.
   *
   * @param count - The number of suggestions to get.
   * @returns A promise of the suggestions.
   */
  async getInitialNextMessageSuggestions(count = 2) {
    const url = new URL("suggest", this.baseURL);
    const context = await this.options.getContext();
    const body = {
      history: this.history,
      context,
      isInitial: true,
      count
    };
    try {
      const { data } = await this.post(url.toString(), body);
      return data;
    } catch {
      return [];
    }
  }
  async post(url, body) {
    const headers = await this.headersPromise;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-glide-agent-client-version": CLIENT_VERSION.toString(),
          // TODO: Remove this once the agent service is deployed
          // without it.
          "x-glide-agent-support-concurrent": "true",
          ...headers
        },
        body: JSON.stringify(body)
      });
      const error = await getErrorForResponse(response);
      if (error !== void 0)
        throw error;
      return await response.json();
    } catch (err) {
      console.error("Agent", err);
      throw err;
    }
  }
  /**
   * Generate sample rows for a table.
   *
   * @param props - The properties of the table.
   * @returns A promise of the sample rows.
   */
  async generateSampleRows(props) {
    const userId = await this.options.getUserIdToken();
    const runId = this.currentRunIdDefined();
    const url = new URL("generate-sample-rows", this.baseURL);
    const body = { ...props, userId, runId };
    const { data } = await this.post(url.toString(), body);
    return data;
  }
  async generateScreen(args) {
    const { screenKind, userPrompt, table, userTable } = args;
    const gatewayURL = this.options.getGatewayURL("agent-service");
    const url = new URL("screens/generate", `${gatewayURL}/`);
    const userId = await this.options.getUserIdToken();
    const body = {
      screenKind,
      table,
      userTable,
      userPrompt,
      runId: this.currentRunId,
      userId
    };
    const data = await this.post(url.toString(), body);
    const result = {
      components: data.components,
      newScreens: data.newScreens,
      newTemplates: data.newTemplates,
      newColumns: data.newColumns.map((c) => ({
        ...c,
        // This has a default cause we're deploying the client before the service.
        scope: c.scope ?? "table"
      }))
    };
    return result;
  }
  async editScreen(args) {
    const { screenKind, currentComponents, userPrompt, table, userTable } = args;
    const gatewayURL = this.options.getGatewayURL("agent-service");
    const url = new URL("screens/edit", `${gatewayURL}/`);
    const userId = await this.options.getUserIdToken();
    const body = {
      screenKind,
      currentComponents,
      table,
      userTable,
      userPrompt,
      runId: this.currentRunId,
      userId
    };
    const data = await this.post(url.toString(), body);
    return data;
  }
  async sendFeedback({ comment, appID }) {
    const url = new URL("send-feedback", this.baseURL);
    const body = { comment, appID };
    const { data } = await this.post(url.toString(), body);
    return data;
  }
  async createEvalScenario(args) {
    const gatewayURL = this.options.getGatewayURL("agent-service");
    const url = new URL("evals/scenarios", `${gatewayURL}/`);
    await this.post(url.toString(), args);
  }
};
async function collectOutput(gen) {
  const llmMessages = [];
  for await (const result of gen) {
    if (result.type === "message") {
      llmMessages.push(result.content);
    }
  }
  return llmMessages.join("\n");
}

// src/types.ts
import { coreMessageSchema, modelMessageSchema } from "ai";
import { z as z2 } from "zod/v4";
var messageSchema = z2.object({
  content: z2.string()
});
var messageResponseSchema = messageSchema.extend({
  type: z2.literal("message")
});
var messageProgressResponseSchema = messageSchema.extend({
  type: z2.literal("message_progress")
});
var messageHistorySchema = messageResponseSchema.extend({
  role: z2.enum(["user", "assistant", "system"])
});
var toolCallSchema = z2.object({
  type: z2.literal("function_call"),
  name: z2.string(),
  call_id: z2.string(),
  arguments: z2.string(),
  display_name: z2.string().optional(),
  add_to_history: z2.boolean().optional(),
  // default: true
  log_id: z2.string().optional()
  // for humanloop
});
var tableRowSchema = z2.record(z2.string(), z2.unknown());
var textContentSchema = z2.object({
  type: z2.literal("text"),
  text: z2.string()
});
var tableContentSchema = z2.object({
  type: z2.literal("table"),
  rows: z2.array(tableRowSchema)
});
var qrCodeContentSchema = z2.object({
  type: z2.literal("qr-code"),
  url: z2.string().describe("The url to display as a qr code"),
  additionalMessage: z2.string().describe("An optional message to display with the qr code").optional()
});
var editAppThemeContentSchema = z2.object({
  type: z2.literal("edit_app_theme")
});
var importDataContentSchema = z2.object({
  type: z2.literal("import_data")
});
var toolRichContentSchema = z2.union([
  textContentSchema,
  tableContentSchema,
  qrCodeContentSchema,
  editAppThemeContentSchema,
  importDataContentSchema
]);
var frontendEffectSchema = z2.object({
  type: z2.literal("frontend_effect"),
  name: z2.string(),
  arguments: z2.unknown()
});
var toolOutputSchema = z2.object({
  llmMessage: z2.string().describe("Passed to parent agent"),
  richContent: z2.array(toolRichContentSchema).describe("Maybe shown on the UI. Parent agent does not see this"),
  error: z2.boolean().optional(),
  frontendEffect: frontendEffectSchema.optional()
});
var toolCallResultSchema = z2.object({
  type: z2.literal("function_call_output"),
  call_id: z2.string(),
  output: toolOutputSchema,
  add_to_history: z2.boolean().optional(),
  // default: true
  log_id: z2.string().optional()
  // for humanloop
});
var legacyCoreMessageSchema = z2.union([
  modelMessageSchema,
  // We want to move entirely to the AI SDK CoreMessage type.
  // For now we include it here to make it easier to migrate.
  coreMessageSchema,
  // These are the old types that we want to migrate away from.
  messageHistorySchema,
  toolCallSchema,
  toolCallResultSchema
]);
var draftStateSchema = z2.object({
  state: z2.literal("draft")
});
var publishedStateSchema = z2.object({
  state: z2.literal("published"),
  url: z2.string()
});
var publishedAppSchema = z2.union([draftStateSchema, publishedStateSchema]);
var turnContextSchema = z2.object({
  mode: z2.string().optional(),
  currentScreenName: z2.string().optional(),
  // This is now only used for internal purposes and should not be sent from the frontend.
  extraContext: z2.record(z2.string(), z2.unknown()).optional(),
  client: z2.object({
    formFactor: z2.enum(["large", "mobile"])
  }).optional(),
  user: z2.object({
    displayName: z2.string(),
    distinctId: z2.string().optional().describe(
      "The unique identifier for the user for analytics (e.g. should be the same as the distinct_id passed to PostHog to identify the user)"
    ),
    emails: z2.array(z2.string()),
    appUserID: z2.string().optional().describe("Only needed when running in the player (e.g. data studio)")
  }).optional(),
  org: z2.object({
    displayName: z2.string(),
    id: z2.string().optional()
  }).optional(),
  app: z2.object({
    appID: z2.string(),
    name: z2.string(),
    description: z2.string().optional(),
    publishState: publishedAppSchema.optional()
  }).optional()
});
var turnRequestSchema = z2.object({
  runId: z2.string().optional(),
  context: turnContextSchema,
  history: z2.array(legacyCoreMessageSchema).min(1)
});
var turnResponseSchema = z2.union([
  messageResponseSchema,
  toolCallSchema,
  toolCallResultSchema,
  frontendEffectSchema
]);
var streamingTurnResponseSchema = z2.union([turnResponseSchema, messageProgressResponseSchema]);

// src/use-chat.ts
import { useChat as useVercelChat } from "@ai-sdk/react";
import {
  convertToModelMessages,
  DefaultChatTransport as VercelDefaultChatTransport
} from "ai";
import { getToolName } from "ai";
var DefaultChatTransport = class extends VercelDefaultChatTransport {
  constructor(options) {
    super({
      headers: {
        "x-glide-agent-support-concurrent": "true",
        "x-glide-agent-client-version": CLIENT_VERSION.toString(),
        ...options.headers
      },
      ...options
    });
  }
};
function glideUIMessagesToModelMessages(messages) {
  return convertToModelMessages(messages);
}
function useChat(options) {
  return useVercelChat(options);
}
export {
  AgentClient as Agent,
  CLIENT_VERSION,
  DefaultChatTransport,
  PaymentRequiredError,
  columnTypeSchema,
  editAppThemeContentSchema,
  errorOutput,
  frontendEffectSchema,
  generateOutput,
  getToolName,
  glideUIMessagesToModelMessages,
  importDataContentSchema,
  isToolOutput,
  legacyCoreMessageSchema,
  messageHistorySchema,
  messageOutput,
  messageProgressResponseSchema,
  messageResponseSchema,
  qrCodeContentSchema,
  rowSchema,
  rowValueSchema,
  streamingTurnResponseSchema,
  tableContentSchema,
  tableRowSchema,
  textContentSchema,
  textOutput,
  toolCallResultSchema,
  toolCallSchema,
  toolOutputSchema,
  toolRichContentSchema,
  turnContextSchema,
  turnRequestSchema,
  turnResponseSchema,
  useChat
};
